import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { StatusBadge } from '../components/StatusBadge';
import { formatIDR, formatIDR2, formatDateID, formatDateLongID, toInputDate, fromInputDate } from '../lib/format';
import { ArrowLeft, Send, FileDown, MessageCircle, Plus, Trash2, Loader2, Wallet, Receipt, ExternalLink, Download } from 'lucide-react';
import { toast } from 'sonner';
import { buildWhatsAppInvoice } from '../lib/whatsapp';
import { useAuth } from '../context/AuthContext';
import { PERMISSIONS } from '../lib/constants';
import { InvoiceFormDialog } from '../components/InvoiceFormDialog';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { NumberInput } from '../components/NumberInput';

const blankPayment = {
  payment_date: '',
  amount: 0,
  payment_method: 'Bank Transfer',
  payment_reference_number: '',
  proof_file_base64: null,
  proof_file_name: null,
  notes: '',
};

const PROOF_ALLOWED_MIMES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
const PROOF_ALLOWED_EXT = /\.(jpe?g|png|pdf)$/i;
const PROOF_MAX_BYTES = 5 * 1024 * 1024; // 5 MB

const validateProofFile = (f) => {
  if (!f) return 'File tidak ditemukan';
  const mime = (f.type || '').toLowerCase();
  const name = f.name || '';
  if (!PROOF_ALLOWED_MIMES.includes(mime) && !PROOF_ALLOWED_EXT.test(name)) {
    return 'File tidak didukung. Gunakan JPG, PNG, atau PDF.';
  }
  if (f.size > PROOF_MAX_BYTES) {
    return 'Ukuran file maksimal 5MB.';
  }
  return null;
};

export default function InvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState(blankPayment);
  const [proofView, setProofView] = useState(null);  // {src, name, payment} | null
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/invoices/${id}`);
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const openAddPayment = () => {
    const expected = data?.include_withholding_tax
      ? (data?.net_amount_to_collect ?? data?.outstanding_amount ?? 0)
      : (data?.outstanding_amount ?? 0);
    setPayForm({ ...blankPayment, payment_date: toInputDate(new Date()), amount: data?.outstanding_amount || expected || 0 });
    setPayOpen(true);
  };

  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const err = validateProofFile(f);
    if (err) { toast.error(err); e.target.value = ''; return; }
    const reader = new FileReader();
    reader.onload = () => {
      setPayForm(p => ({ ...p, proof_file_base64: reader.result, proof_file_name: f.name }));
    };
    reader.readAsDataURL(f);
  };

  const savePayment = async (e) => {
    e.preventDefault();
    if (!payForm.amount || Number(payForm.amount) <= 0) { toast.error('Jumlah harus > 0'); return; }
    setSaving(true);
    try {
      await api.post('/payments', {
        invoice_id: id,
        payment_date: fromInputDate(payForm.payment_date) || new Date().toISOString(),
        amount: Number(payForm.amount),
        payment_method: payForm.payment_method,
        payment_reference_number: payForm.payment_reference_number || '',
        proof_file_base64: payForm.proof_file_base64,
        proof_file_name: payForm.proof_file_name,
        notes: payForm.notes || '',
      });
      toast.success('Pembayaran ditambahkan');
      setPayOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal');
    } finally { setSaving(false); }
  };

  const deletePayment = async (pid) => {
    try {
      await api.delete(`/payments/${pid}`);
      toast.success('Pembayaran dihapus');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal');
    }
  };

  const copyWhatsApp = async () => {
    if (!data) return;
    const msg = buildWhatsAppInvoice({
      client_name: data.client_name,
      event_description: data.event_description,
      event_date: data.event_date,
      venue: data.venue,
      invoice_number: data.invoice_number,
      invoice_amount: data.invoice_amount,
      outstanding_amount: data.outstanding_amount,
      due_date: data.due_date,
      payment_instructions: data.payment_instructions,
    });
    try { await navigator.clipboard.writeText(msg); toast.success('Pesan WA tersalin'); }
    catch { toast.error('Gagal menyalin'); }
  };

  const setInvoiceStatus = async (s) => {
    try {
      await api.post(`/invoices/${id}/status`, null, { params: { new_status: s } });
      toast.success('Status diperbarui');
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Gagal'); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${data?.invoice_number || 'invoice'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('PDF belum tersedia');
    }
  };

  if (loading) return <div className="space-y-3"><Skeleton className="h-10 w-32" /><Skeleton className="h-40" /><Skeleton className="h-60" /></div>;
  if (!data) return null;
  const canEdit = PERMISSIONS.canCreateInvoice(role);
  const canPay = PERMISSIONS.canManagePayments(role);
  const canDeleteFin = PERMISSIONS.canDeleteFinancial(role);

  return (
    <div className="space-y-6" data-testid="invoice-detail-page">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/invoices')}><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-heading text-xl font-semibold font-mono-num">{data.invoice_number}</span>
            <StatusBadge status={data.status} />
            {data.include_ppn && (
              <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-medium" data-testid="invoice-ppn-badge">
                PPN
              </span>
            )}
            {data.include_withholding_tax && (
              <span className="inline-flex items-center rounded-full border border-amber-400/30 bg-amber-400/10 text-amber-300 px-2 py-0.5 text-[10px] font-medium" data-testid="invoice-pph-badge">
                {data.withholding_tax_type || 'PPh'} {Number(data.withholding_tax_rate || 0)}%
              </span>
            )}
          </div>
          <div className="text-sm text-muted-foreground">{data.client_name} • {formatDateLongID(data.invoice_date)}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canEdit && <Button variant="secondary" onClick={() => setEditOpen(true)}>Edit</Button>}
          {canEdit && data.status === 'Draft' && <Button variant="secondary" onClick={() => setInvoiceStatus('Sent')}><Send className="h-4 w-4 mr-2" />Tandai Terkirim</Button>}
          {canPay && data.outstanding_amount > 0 && <Button onClick={openAddPayment} data-testid="invoice-add-payment"><Wallet className="h-4 w-4 mr-2" />Tambah Pembayaran</Button>}
          <Button variant="secondary" onClick={copyWhatsApp}><MessageCircle className="h-4 w-4 mr-2" />Pesan WA</Button>
          <Button variant="secondary" onClick={downloadPdf}><FileDown className="h-4 w-4 mr-2" />PDF</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-card border-border md:col-span-2">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Client & Event</CardTitle></CardHeader>
          <CardContent className="text-sm grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div><span className="text-muted-foreground">Client:</span> <span className="font-medium">{data.client?.client_name}</span></div>
            <div><span className="text-muted-foreground">PIC:</span> {data.client?.pic_name || '-'}</div>
            <div><span className="text-muted-foreground">Event:</span> {data.event_description || '-'}</div>
            <div><span className="text-muted-foreground">Tgl Event:</span> {formatDateID(data.event_date)}</div>
            <div><span className="text-muted-foreground">Venue:</span> {data.venue || '-'}</div>
            <div><span className="text-muted-foreground">Jatuh Tempo:</span> {formatDateID(data.due_date)}</div>
            {data.payment_instructions && <div className="sm:col-span-2 mt-2"><div className="text-xs text-muted-foreground">Instruksi Pembayaran</div><div className="whitespace-pre-wrap">{data.payment_instructions}</div></div>}
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Ringkasan Pajak (e-Faktur)</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1.5">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono-num">{formatIDR2(data.subtotal_amount || data.invoice_amount)}</span></div>
            {(data.final_discount_amount || 0) > 0 && (
              <div className="flex justify-between text-muted-foreground"><span>Diskon Akhir</span><span className="font-mono-num">-{formatIDR2(data.final_discount_amount)}</span></div>
            )}
            <div className="flex justify-between"><span className="text-muted-foreground">DPP</span><span className="font-mono-num">{formatIDR2(data.dpp_amount || data.invoice_amount || 0)}</span></div>
            {data.include_ppn && (
              <>
                <div className="flex justify-between text-muted-foreground"><span>DPP Nilai Lain</span><span className="font-mono-num">{formatIDR2(data.dpp_nilai_lain_amount || 0)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">PPN</span><span className="font-mono-num">{formatIDR2(data.ppn_amount || 0)}</span></div>
              </>
            )}
            <div className="flex justify-between text-base pt-1.5 border-t border-border text-primary">
              <span className="font-semibold">Grand Total</span>
              <span className="font-mono-num font-semibold">{formatIDR2(data.invoice_gross_amount || data.grand_total_amount || data.invoice_amount)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Separate Potongan/Collection summary (PPh does NOT reduce Grand Total) */}
        <Card className={`border-border ${data.include_withholding_tax ? 'bg-amber-400/5 border-amber-400/30' : 'bg-card'}`} data-testid="payment-collection-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Pembayaran & Potongan</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1.5">
            <div className="flex justify-between"><span className="text-muted-foreground">Grand Total</span><span className="font-mono-num">{formatIDR2(data.invoice_gross_amount || data.grand_total_amount || data.invoice_amount)}</span></div>
            {data.include_withholding_tax && (
              <div className="flex justify-between text-amber-300">
                <span>PPh Dipotong</span>
                <span className="font-mono-num">-{formatIDR2(data.withholding_tax_amount || 0)}</span>
              </div>
            )}
            <div className="flex justify-between text-base pt-1.5 border-t border-border text-emerald-300">
              <span className="font-semibold">Jumlah yang Harus Dibayar Client</span>
              <span className="font-mono-num font-semibold">{formatIDR2(data.net_amount_to_collect || data.invoice_gross_amount || data.invoice_amount || 0)}</span>
            </div>
            <div className="flex justify-between"><span className="text-muted-foreground">Paid Amount</span><span className="font-mono-num text-emerald-300">{formatIDR2(data.paid_amount || 0)}</span></div>
            <div className="flex justify-between text-base pt-1.5 border-t border-border"><span className="font-semibold">Outstanding</span><span className="font-mono-num font-semibold">{formatIDR2(data.outstanding_amount || 0)}</span></div>
            {data.include_withholding_tax && (
              <div className="flex justify-between text-xs pt-1">
                <span className="text-muted-foreground">Status Bukti Potong</span>
                <span className={`font-medium ${data.withholding_status === 'Received' ? 'text-emerald-300' : 'text-amber-300'}`}>{data.withholding_status || 'Waiting'}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="bg-card border-border">
        <CardHeader className="pb-2"><CardTitle className="text-sm font-heading">Line Items</CardTitle></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">Nama</th>
                  <th className="px-4 py-2 text-left">Deskripsi</th>
                  <th className="px-4 py-2 text-right">Qty</th>
                  <th className="px-4 py-2 text-right">Harga</th>
                  <th className="px-4 py-2 text-right">Diskon</th>
                  <th className="px-4 py-2 text-right">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {data.items?.map((it) => (
                  <tr key={it.id} className="border-t border-border">
                    <td className="px-4 py-2">{it.item_name}</td>
                    <td className="px-4 py-2 text-muted-foreground whitespace-pre-wrap align-top">{it.description || '-'}</td>
                    <td className="px-4 py-2 text-right font-mono-num">{it.quantity}</td>
                    <td className="px-4 py-2 text-right font-mono-num">{formatIDR(it.unit_price)}</td>
                    <td className="px-4 py-2 text-right font-mono-num">{formatIDR(it.discount)}</td>
                    <td className="px-4 py-2 text-right font-mono-num">{formatIDR(it.subtotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-heading">Riwayat Pembayaran</CardTitle>
          {canPay && data.outstanding_amount > 0 && <Button size="sm" onClick={openAddPayment}><Plus className="h-3.5 w-3.5 mr-1" />Tambah</Button>}
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">No</th>
                  <th className="px-4 py-2 text-left">Tgl</th>
                  <th className="px-4 py-2 text-left">Metode</th>
                  <th className="px-4 py-2 text-left">Referensi</th>
                  <th className="px-4 py-2 text-right">Jumlah</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {(!data.payments || data.payments.length === 0) && <tr><td colSpan={6} className="text-center py-6 text-muted-foreground">Belum ada pembayaran.</td></tr>}
                {data.payments?.map(p => (
                  <tr key={p.id} className="border-t border-border">
                    <td className="px-4 py-2 font-mono-num text-xs">{p.payment_number}</td>
                    <td className="px-4 py-2">{formatDateID(p.payment_date)}</td>
                    <td className="px-4 py-2">{p.payment_method}</td>
                    <td className="px-4 py-2 text-muted-foreground">{p.payment_reference_number || '-'}</td>
                    <td className="px-4 py-2 text-right font-mono-num">{formatIDR(p.amount)}</td>
                    <td className="px-4 py-2 text-right">
                      {p.proof_file_base64 && (
                        <button
                          type="button"
                          onClick={() => setProofView({ src: p.proof_file_base64, name: p.proof_file_name || 'Bukti Transfer', payment: p })}
                          className="inline-flex items-center gap-1 text-xs text-primary hover:underline mr-2"
                          data-testid={`payment-view-proof-${p.id}`}
                        >
                          <Receipt className="h-3.5 w-3.5" />
                          Lihat Bukti Transfer
                        </button>
                      )}
                      {canDeleteFin && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7 text-red-300"><Trash2 className="h-3.5 w-3.5" /></Button></AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader><AlertDialogTitle>Hapus pembayaran?</AlertDialogTitle><AlertDialogDescription>Outstanding invoice akan dihitung ulang.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={() => deletePayment(p.id)}>Hapus</AlertDialogAction></AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {data.include_withholding_tax && (
        <Card className="bg-card border-border" data-testid="bukti-potong-card">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-heading">Bukti Potong</CardTitle>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${
              data.withholding_status === 'Received'
                ? 'bg-emerald-400/15 text-emerald-300 border-emerald-400/30'
                : 'bg-amber-400/15 text-amber-300 border-amber-400/30'
            }`}>
              {data.withholding_status || 'Waiting'}
            </span>
          </CardHeader>
          <CardContent>
            <BuktiPotongForm invoice={data} canEdit={canEdit} onUpdated={load} />
          </CardContent>
        </Card>
      )}

      <InvoiceFormDialog open={editOpen} onOpenChange={setEditOpen} editing={data} onSaved={load} />

      {/* Payment Dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-lg bg-card border-border">
          <DialogHeader><DialogTitle>Tambah Pembayaran</DialogTitle></DialogHeader>
          <form onSubmit={savePayment} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Tanggal</Label>
                <Input type="date" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Metode</Label>
                <Select value={payForm.payment_method} onValueChange={(v) => setPayForm({ ...payForm, payment_method: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Bank Transfer">Transfer Bank</SelectItem>
                    <SelectItem value="Cash">Tunai</SelectItem>
                    <SelectItem value="QRIS">QRIS</SelectItem>
                    <SelectItem value="E-Wallet">E-Wallet</SelectItem>
                    <SelectItem value="Other">Lainnya</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs">Jumlah (Rp)</Label>
                <NumberInput testid="payment-amount-input" className="h-11 md:h-9 text-base md:text-sm text-right font-mono-num" value={payForm.amount} onChange={(v) => setPayForm({ ...payForm, amount: v })} />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs">No Referensi</Label>
                <Input value={payForm.payment_reference_number} onChange={(e) => setPayForm({ ...payForm, payment_reference_number: e.target.value })} />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs">Upload Bukti Transfer (opsional)</Label>
                <div className="rounded-lg border border-dashed border-border bg-card/40 p-3 space-y-2">
                  <Input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                    onChange={onFileChange}
                    data-testid="payment-proof-input"
                    className="cursor-pointer"
                  />
                  <div className="text-[11px] text-muted-foreground">
                    Format: JPG, PNG, PDF · Maks 5MB
                  </div>
                  {payForm.proof_file_name && (
                    <div className="flex items-center justify-between gap-2 rounded-md bg-emerald-400/10 border border-emerald-400/30 px-2 py-1.5 text-xs">
                      <span className="truncate text-emerald-200 flex-1" data-testid="payment-proof-name">
                        📎 {payForm.proof_file_name}
                      </span>
                      <button
                        type="button"
                        onClick={() => setPayForm(p => ({ ...p, proof_file_base64: null, proof_file_name: null }))}
                        className="text-emerald-300 hover:text-red-300 transition-colors px-1"
                        data-testid="payment-proof-clear"
                        aria-label="Hapus Bukti Transfer"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-xs">Catatan</Label>
                <Textarea rows={2} value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setPayOpen(false)}>Batal</Button>
              <Button type="submit" disabled={saving} data-testid="payment-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Simpan</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Proof Viewer Dialog */}
      <Dialog open={!!proofView} onOpenChange={(o) => { if (!o) setProofView(null); }}>
        <DialogContent className="max-w-3xl bg-card border-border" data-testid="proof-viewer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-4 w-4 text-primary" />
              Bukti Transfer
            </DialogTitle>
            <DialogDescription>
              {proofView?.payment?.payment_number} · {proofView?.payment ? formatDateID(proofView.payment.payment_date) : ''} · {proofView?.payment ? formatIDR(proofView.payment.amount) : ''}
            </DialogDescription>
          </DialogHeader>
          {proofView && (
            <div className="space-y-3">
              <div className="rounded-lg border border-border bg-black/40 overflow-hidden max-h-[60vh] flex items-center justify-center">
                {proofView.src?.startsWith('data:application/pdf') || proofView.name?.toLowerCase().endsWith('.pdf') ? (
                  <iframe
                    src={proofView.src}
                    title={proofView.name}
                    className="w-full h-[60vh]"
                  />
                ) : (
                  <img
                    src={proofView.src}
                    alt={proofView.name}
                    className="max-w-full max-h-[60vh] object-contain"
                  />
                )}
              </div>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs text-muted-foreground truncate flex-1">
                  📎 {proofView.name}
                </div>
                <div className="flex gap-2">
                  <a
                    href={proofView.src}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-border bg-card hover:border-primary/40 text-xs"
                    data-testid="proof-view-open-tab"
                  >
                    <ExternalLink className="h-3.5 w-3.5" /> Buka di Tab Baru
                  </a>
                  <a
                    href={proofView.src}
                    download={proofView.name}
                    className="inline-flex items-center gap-1 h-9 px-3 rounded-md bg-primary text-primary-foreground hover:opacity-90 text-xs"
                    data-testid="proof-view-download"
                  >
                    <Download className="h-3.5 w-3.5" /> Download
                  </a>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const BuktiPotongForm = ({ invoice, canEdit, onUpdated }) => {
  const [form, setForm] = React.useState({
    withholding_status: invoice.withholding_status || 'Waiting',
    withholding_certificate_number: invoice.withholding_certificate_number || '',
    withholding_certificate_date: toInputDate(invoice.withholding_certificate_date) || '',
    withholding_certificate_file_base64: invoice.withholding_certificate_file_base64 || null,
    withholding_certificate_file_name: invoice.withholding_certificate_file_name || null,
  });
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setForm({
      withholding_status: invoice.withholding_status || 'Waiting',
      withholding_certificate_number: invoice.withholding_certificate_number || '',
      withholding_certificate_date: toInputDate(invoice.withholding_certificate_date) || '',
      withholding_certificate_file_base64: invoice.withholding_certificate_file_base64 || null,
      withholding_certificate_file_name: invoice.withholding_certificate_file_name || null,
    });
  }, [invoice.id, invoice.withholding_status, invoice.withholding_certificate_number, invoice.withholding_certificate_date]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 3 * 1024 * 1024) { toast.error('Maks 3 MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(p => ({ ...p, withholding_certificate_file_base64: reader.result, withholding_certificate_file_name: f.name }));
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/invoices/${invoice.id}/withholding`, {
        withholding_status: form.withholding_status,
        withholding_certificate_number: form.withholding_certificate_number || '',
        withholding_certificate_date: fromInputDate(form.withholding_certificate_date),
        withholding_certificate_file_base64: form.withholding_certificate_file_base64,
        withholding_certificate_file_name: form.withholding_certificate_file_name,
      });
      toast.success('Bukti potong diperbarui');
      onUpdated && onUpdated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal');
    } finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
      <div className="space-y-1.5">
        <Label className="text-xs">Status Bukti Potong</Label>
        <Select value={form.withholding_status} onValueChange={(v) => setForm({ ...form, withholding_status: v })}>
          <SelectTrigger className="h-11 md:h-9 text-base md:text-sm" data-testid="bukti-potong-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Waiting">Waiting</SelectItem>
            <SelectItem value="Received">Received</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Nomor Bukti Potong</Label>
        <Input
          className="h-11 md:h-9 text-base md:text-sm"
          value={form.withholding_certificate_number}
          onChange={(e) => setForm({ ...form, withholding_certificate_number: e.target.value })}
          data-testid="bukti-potong-number"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Tanggal Bukti Potong</Label>
        <Input
          type="date"
          className="h-11 md:h-9 text-base md:text-sm date-input-mobile"
          value={form.withholding_certificate_date}
          onChange={(e) => setForm({ ...form, withholding_certificate_date: e.target.value })}
          data-testid="bukti-potong-date"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs">Upload Bukti Potong (opsional, max 3MB)</Label>
        <Input type="file" accept="image/*,application/pdf" onChange={onFile} />
        {form.withholding_certificate_file_name && (
          <div className="text-xs text-muted-foreground flex items-center gap-2">
            <span>{form.withholding_certificate_file_name}</span>
            {form.withholding_certificate_file_base64 && (
              <a href={form.withholding_certificate_file_base64} target="_blank" rel="noreferrer" className="text-primary hover:underline">Lihat</a>
            )}
          </div>
        )}
      </div>
      <div className="sm:col-span-2 flex justify-end">
        <Button onClick={save} disabled={!canEdit || saving} data-testid="bukti-potong-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Simpan
        </Button>
      </div>
    </div>
  );
};
